/**
 * Native market signing helpers for gasless sells and claims
 *
 * Uses EIP-712 signatures verified directly by the BreakoutMarket contract.
 */
import type { Address } from "viem"
import { readContract } from "viem/actions"
import { breakoutMarketAbi } from "../../abi/generated/breakoutMarket"
import type { EIP712Domain, SigningPayload } from "../../types"
import type { ReadableClient } from "../contracts"

// =============================================================================
// EIP-712 Types
// =============================================================================

/**
 * Sell(address trader,uint8 bucket,uint256 shares,uint256 minProceeds,uint256 nonce,uint256 deadline)
 */
export const SELL_TYPES = {
	Sell: [
		{ name: "trader", type: "address" },
		{ name: "bucket", type: "uint8" },
		{ name: "shares", type: "uint256" },
		{ name: "minProceeds", type: "uint256" },
		{ name: "nonce", type: "uint256" },
		{ name: "deadline", type: "uint256" },
	],
} as const

/**
 * Claim(address trader,uint256 nonce,uint256 deadline)
 */
export const CLAIM_TYPES = {
	Claim: [
		{ name: "trader", type: "address" },
		{ name: "nonce", type: "uint256" },
		{ name: "deadline", type: "uint256" },
	],
} as const

// =============================================================================
// Domain
// =============================================================================

/**
 * EIP-712 domain for a market
 * The verifyingContract is the market address
 */
export function getMarketDomain(chainId: number, market: Address): EIP712Domain {
	return {
		name: "BreakoutMarket",
		version: "1",
		chainId,
		verifyingContract: market,
	}
}

// =============================================================================
// Signing Payloads
// =============================================================================

export interface SellSigningMessage {
	trader: Address
	bucket: number
	shares: bigint
	minProceeds: bigint
	nonce: bigint
	deadline: bigint
}

export function buildSellSigningPayload(
	chainId: number,
	market: Address,
	message: SellSigningMessage,
): SigningPayload {
	return {
		domain: getMarketDomain(chainId, market),
		types: SELL_TYPES,
		primaryType: "Sell",
		message: {
			trader: message.trader,
			bucket: message.bucket,
			shares: message.shares,
			minProceeds: message.minProceeds,
			nonce: message.nonce,
			deadline: message.deadline,
		},
	}
}

export interface ClaimSigningMessage {
	trader: Address
	nonce: bigint
	deadline: bigint
}

export function buildClaimSigningPayload(
	chainId: number,
	market: Address,
	message: ClaimSigningMessage,
): SigningPayload {
	return {
		domain: getMarketDomain(chainId, market),
		types: CLAIM_TYPES,
		primaryType: "Claim",
		message: {
			trader: message.trader,
			nonce: message.nonce,
			deadline: message.deadline,
		},
	}
}

// =============================================================================
// Nonce Management
// =============================================================================

/**
 * Check if a nonce has been used for a user on a market
 */
export async function isNonceUsed(
	client: ReadableClient,
	market: Address,
	user: Address,
	nonce: bigint,
): Promise<boolean> {
	return readContract(client, {
		address: market,
		abi: breakoutMarketAbi,
		functionName: "usedNonces",
		args: [user, nonce],
	})
}

/**
 * Generate a random nonce for gasless operations
 * Uses timestamp + random to avoid collisions
 */
export function generateNonce(): bigint {
	return BigInt(Date.now()) * 1000n + BigInt(Math.floor(Math.random() * 1000))
}
